import * as React from 'react';
import BuildIcon from '@material-ui/icons/Build';
import CloseIcon from '@material-ui/icons/Close';
import Drawer from '@material-ui/core/Drawer';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Logo from './Logo';
import PersonIcon from '@material-ui/icons/Person';
import SchoolIcon from '@material-ui/icons/School';
import { AnchorListItem } from './AnchorListItem';
import { DeepReadonly } from 'utility-types';
import { DrawerLinksContainer } from '../../containers/Nav/DrawerLinks';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { IOrganization } from '@pec/aion-ui-core/interfaces/organization';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { ListItem } from './ListItem';
import { Location } from 'history';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

const styles = (theme: Theme) => ({
  close: {
    marginTop: -theme.spacing(1) / 2
  },
  closeIcon: {
    color: theme.palette.common.white
  },
  logo: {
    color: theme.palette.primary.main,
    marginLeft: -6
  },
  paper: {
    maxWidth: '100%'
  },
  menuHeader: {
    backgroundColor: '#002E41'
  },
  nav: {
    paddingTop: 0
  }
});

type Props = WithStyles<typeof styles> & OwnProps;

export type OwnProps = {
  handleLogOut: () => void;
  toggleDrawer: () => void;
  open: boolean;
  sideNav?: JSX.Element;
  organizationId?: string;
  location: Location;
  userOrganizations: DeepReadonly<IUserOrganization[]> | null;
  organization: DeepReadonly<IOrganization> | null;
};

const MobileDrawerComponent: React.FC<Props> = ({
  handleLogOut,
  toggleDrawer,
  open,
  sideNav,
  organization,
  organizationId,
  classes,
  location,
  userOrganizations
}) => {
  const { t } = useTranslation();
  const vf1MyProfileLink = '/?hint=vf1profile';
  const myAccountLink = '/identity/Manage/Index';

  return (
    <Drawer
      classes={{
        paper: classes.paper
      }}
      variant="temporary"
      anchor="left"
      open={open}
      onClose={toggleDrawer}
    >
      <div tabIndex={0} role="button" onClick={toggleDrawer} onKeyDown={toggleDrawer}>
        <List component="nav" className={classes.nav}>
          <ListItem divider className={classes.menuHeader}>
            <Logo
              organizationId={organizationId}
              userOrganizations={userOrganizations}
              className={classes.logo}
              organization={organization}
            />
            <ListItemSecondaryAction className={classes.close}>
              <IconButton
                aria-label={t('components.mobileDrawer.close', 'Close')}
                className={classes.closeIcon}
                edge="end"
              >
                <CloseIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          <DrawerLinksContainer navExpanded organizationId={organizationId} sideNav={sideNav} location={location} />
          <FeatureFlagWrapper>
            <Feature
              name={'VF1MyProfile'}
              render={(isEnabled: boolean) => (
                <AnchorListItem divider href={isEnabled ? vf1MyProfileLink : myAccountLink} icon={<PersonIcon />}>
                  {t('components.mobileDrawer.myAccount', 'My Account')}
                </AnchorListItem>
              )}
            />
          </FeatureFlagWrapper>
          <AnchorListItem divider href="/user" icon={<BuildIcon />}>
            {t('components.mobileDrawer.myProfile', 'My Profile')}
          </AnchorListItem>
          <AnchorListItem divider href="/eLearning" icon={<SchoolIcon />}>
            {t('components.mobileDrawer.eLearning', 'eLearning')}
          </AnchorListItem>
          <AnchorListItem divider onClick={handleLogOut} icon={<ExitToAppIcon />}>
            {t('components.mobileDrawer.logOut', 'Log Out')}
          </AnchorListItem>
        </List>
      </div>
    </Drawer>
  );
};

export const MobileDrawer = withStyles(styles)(MobileDrawerComponent);
